import React from "react";
import {
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import { Checkbox, FormControlLabel, IconButton, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import moment from "moment";

import { Controller, useForm } from "react-hook-form";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import DateRangeToggle from "components/Shared/DateRangeToggle";
import { useLazyListVehiclesQuery } from "store/services/vehicleApi";
import { exportExcelFile } from "utils/exportExcelFile";

const EXCEL_DATA_INITIAL_STATE = { dateRange: [], insurance_id: null };

const ExportVehicleExcel = () => {
  const [showDialog, setShowDialog] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: EXCEL_DATA_INITIAL_STATE,
  });

  const [fetchVehicles] = useLazyListVehiclesQuery();

  const handleDownload = async (data) => {
    const { dateRange } = data;

    const fromDate = moment(dateRange[0]).format("MM-DD-YYYY");
    const toDate = moment(dateRange[1]).format("MM-DD-YYYY");

    const payload = {
      insurance_id: data?.insurance_id?._id,
      fromDate,
      toDate,
    };

    try {
      const responseDate = await fetchVehicles(payload).unwrap();
      if (!responseDate.data || responseDate.data.length === 0) {
        toast.error("No Data Found!");
        return;
      }

      // Group vehicles and ensure vehicleInsurance is an array, similar to VehicleListScreen
      const groupedVehicles = (responseDate.data ?? []).reduce((acc, vehicle) => {
        const { vehicle_id } = vehicle;
        if (!acc[vehicle_id]) {
          acc[vehicle_id] = {
            ...vehicle,
            vehicleInsurance: Array.isArray(vehicle.vehicleInsurance)
              ? vehicle.vehicleInsurance
              : vehicle?.vehicleInsurance
                ? [vehicle.vehicleInsurance]
                : [],
          };
        } else {
          acc[vehicle_id].vehicleInsurance = [
            ...acc[vehicle_id].vehicleInsurance,
            ...(Array.isArray(vehicle.vehicleInsurance)
              ? vehicle.vehicleInsurance
              : vehicle?.vehicleInsurance
                ? [vehicle.vehicleInsurance]
                : []),
          ];
        }
        return acc;
      }, {});

      const groupedVehiclesArray = Object.values(groupedVehicles);

      // Map the grouped data to the Excel format
      const formattedData = groupedVehiclesArray.map((row, index) => ({
        SN: index + 1,
        "Vehicle ID": row.vehicle_id || "N/A",
        "Vehicle Type": row.vehicle_type || "N/A",
        "Insurance ID":
          row.vehicleInsurance
            ?.filter((insurance) => insurance?.status)
            .map((insurance) => insurance?.insurance_id)
            .filter(Boolean)
            .join(", ") || "N/A",
        Capacity: row.capacity || "N/A",
        "Registration Number": row.registration_number || "N/A",
        "Maintenance Due Date": row.maintenance_due_date
          ? new Date(row.maintenance_due_date).toLocaleDateString()
          : "N/A",
        "Fuel Level": row.fuel_level || "N/A",
        "Ownership Type": row.ownership_type || "N/A",
        "Created Date": row.createdAt
          ? new Date(row.createdAt).toISOString().split("T")[0]
          : "N/A",
        Status: row.status ? "Active" : "Inactive",
      }));

      exportExcelFile({
        sheetData: formattedData,
        fileName: `Vehicles_${data?.insurance_id?.insurance_id || "All"}_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`,
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export data!");
    } finally {
      handleReset();
    }
  };

  const handleReset = () => {
    setShowDialog(false);
    reset(EXCEL_DATA_INITIAL_STATE);
  };

  return (
    <>
      <Tooltip title="Download Excel" arrow>
        <IconButton color="success" onClick={() => setShowDialog(true)}>
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={showDialog}
        onClose={handleReset}
        onConfirm={handleSubmit(handleDownload)}
        title="Download Vehicles"
        dialogIcon={() => <FileDownloadOutlined color="success" />}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={showForm}
              onChange={(e) => setShowForm(e.target.checked)}
              icon={<CheckBoxOutlineBlankOutlined />}
              checkedIcon={<CheckBoxOutlined />}
            />
          }
          label="Filter by specific Insurance"
        />

        <Controller
          name="dateRange"
          control={control}
          rules={{ required: "Date range is required" }}
          render={({ field, fieldState: { error } }) => (
            <DateRangeToggle
              {...field}
              label="Select Date Range"
              error={!!error}
              helperText={error?.message}
              required
            />
          )}
        />
      </AppConfirmDialog>
    </>
  );
};

export default ExportVehicleExcel;